'use client'

import { useEffect, useRef, useState } from 'react'
import Script from 'next/script'
import { useTranslations } from 'next-intl'
import { MaterialIcon } from '@/components/ui/MaterialIcon'

type TurnstileOptions = {
    sitekey: string
    callback: (token: string) => void
    'expired-callback'?: () => void
    'error-callback'?: () => void
    theme?: 'light' | 'dark' | 'auto'
}

declare global {
    interface Window {
        turnstile?: {
            render: (el: HTMLElement, opts: TurnstileOptions) => string
            reset: (id?: string) => void
            remove: (id?: string) => void
        }
    }
}

const SITE_KEY = process.env.NEXT_PUBLIC_TURNSTILE_SITE_KEY

type Status = 'idle' | 'sending' | 'success' | 'error'

export function ContactForm() {
    const t = useTranslations('Contact')
    const [status, setStatus] = useState<Status>('idle')
    const [token, setToken] = useState('')
    const [scriptLoaded, setScriptLoaded] = useState(false)
    const widgetRef = useRef<HTMLDivElement>(null)
    const widgetIdRef = useRef<string | null>(null)

    // The Turnstile script may already be present on client-side navigation
    // back to /contact, in which case <Script onLoad> won't fire again.
    useEffect(() => {
        if (window.turnstile) setScriptLoaded(true)
    }, [])

    useEffect(() => {
        if (!scriptLoaded || !SITE_KEY) return
        if (!widgetRef.current || !window.turnstile) return
        if (widgetIdRef.current) return

        widgetIdRef.current = window.turnstile.render(widgetRef.current, {
            sitekey: SITE_KEY,
            theme: 'light',
            callback: (tok: string) => setToken(tok),
            'expired-callback': () => setToken(''),
            'error-callback': () => setToken(''),
        })

        return () => {
            if (widgetIdRef.current && window.turnstile) {
                window.turnstile.remove(widgetIdRef.current)
                widgetIdRef.current = null
            }
        }
    }, [scriptLoaded])

    function resetCaptcha() {
        setToken('')
        if (widgetIdRef.current && window.turnstile) {
            window.turnstile.reset(widgetIdRef.current)
        }
    }

    async function handleSubmit(e: React.FormEvent<HTMLFormElement>) {
        e.preventDefault()
        if (status === 'sending') return

        const form = e.currentTarget
        const data = new FormData(form)

        if (SITE_KEY && !token) {
            setStatus('error')
            return
        }

        setStatus('sending')
        try {
            const res = await fetch('/api/contact', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                    name: data.get('name'),
                    email: data.get('email'),
                    subject: data.get('subject'),
                    message: data.get('message'),
                    company: data.get('company'),
                    token,
                }),
            })

            if (!res.ok) throw new Error('request_failed')

            setStatus('success')
            form.reset()
            resetCaptcha()
        } catch {
            setStatus('error')
            resetCaptcha()
        }
    }

    if (status === 'success') {
        return (
            <div className="rounded-2xl border border-emerald-200 bg-emerald-50 p-8 text-center">
                <div className="mx-auto mb-4 flex h-14 w-14 items-center justify-center rounded-full bg-emerald-100">
                    <MaterialIcon name="check_circle" className="text-3xl text-emerald-600" />
                </div>
                <h3 className="mb-2 text-xl font-bold text-slate-900">{t('form.successTitle')}</h3>
                <p className="text-slate-600">{t('form.successMessage')}</p>
                <button
                    type="button"
                    onClick={() => setStatus('idle')}
                    className="mt-6 inline-flex items-center gap-2 rounded-lg border border-slate-300 px-5 py-2.5 font-semibold text-slate-700 transition-colors hover:bg-slate-50"
                >
                    {t('form.sendAnother')}
                </button>
            </div>
        )
    }

    return (
        <form onSubmit={handleSubmit} className="rounded-2xl border border-slate-200 bg-white p-8">
            <Script
                id="cf-turnstile-api"
                src="https://challenges.cloudflare.com/turnstile/v0/api.js"
                strategy="afterInteractive"
                onLoad={() => setScriptLoaded(true)}
            />

            <h2 className="mb-2 text-2xl font-bold text-slate-900">{t('form.title')}</h2>
            <p className="mb-6 text-slate-600">{t('form.subtitle')}</p>

            <div className="grid gap-5 sm:grid-cols-2">
                <div>
                    <label htmlFor="name" className="mb-1.5 block text-sm font-semibold text-slate-700">
                        {t('form.nameLabel')}
                    </label>
                    <input
                        id="name"
                        name="name"
                        type="text"
                        required
                        maxLength={120}
                        autoComplete="name"
                        placeholder={t('form.namePlaceholder')}
                        className="w-full rounded-lg border border-slate-300 px-4 py-2.5 text-slate-900 outline-none transition-colors focus:border-primary focus:ring-2 focus:ring-primary/20"
                    />
                </div>
                <div>
                    <label htmlFor="email" className="mb-1.5 block text-sm font-semibold text-slate-700">
                        {t('form.emailLabel')}
                    </label>
                    <input
                        id="email"
                        name="email"
                        type="email"
                        required
                        maxLength={200}
                        autoComplete="email"
                        placeholder={t('form.emailPlaceholder')}
                        className="w-full rounded-lg border border-slate-300 px-4 py-2.5 text-slate-900 outline-none transition-colors focus:border-primary focus:ring-2 focus:ring-primary/20"
                    />
                </div>
            </div>

            <div className="mt-5">
                <label htmlFor="subject" className="mb-1.5 block text-sm font-semibold text-slate-700">
                    {t('form.subjectLabel')}
                </label>
                <input
                    id="subject"
                    name="subject"
                    type="text"
                    maxLength={150}
                    placeholder={t('form.subjectPlaceholder')}
                    className="w-full rounded-lg border border-slate-300 px-4 py-2.5 text-slate-900 outline-none transition-colors focus:border-primary focus:ring-2 focus:ring-primary/20"
                />
            </div>

            <div className="mt-5">
                <label htmlFor="message" className="mb-1.5 block text-sm font-semibold text-slate-700">
                    {t('form.messageLabel')}
                </label>
                <textarea
                    id="message"
                    name="message"
                    required
                    rows={6}
                    maxLength={5000}
                    placeholder={t('form.messagePlaceholder')}
                    className="w-full resize-y rounded-lg border border-slate-300 px-4 py-2.5 text-slate-900 outline-none transition-colors focus:border-primary focus:ring-2 focus:ring-primary/20"
                />
            </div>

            {/* Honeypot — hidden from real users, catches naive bots. */}
            <div className="absolute -left-[9999px]" aria-hidden="true">
                <label htmlFor="company">Company</label>
                <input id="company" name="company" type="text" tabIndex={-1} autoComplete="off" />
            </div>

            <div className="mt-6" ref={widgetRef} />

            {status === 'error' && (
                <p className="mt-4 flex items-center gap-2 text-sm font-medium text-red-600">
                    <MaterialIcon name="error" className="text-base" />
                    {token || !SITE_KEY ? t('form.errorMessage') : t('form.captchaError')}
                </p>
            )}

            <button
                type="submit"
                disabled={status === 'sending'}
                className="mt-6 inline-flex w-full items-center justify-center gap-2 rounded-lg bg-primary px-6 py-3 font-bold text-white transition-colors hover:bg-primary/90 disabled:cursor-not-allowed disabled:opacity-60 sm:w-auto"
            >
                {status === 'sending' ? (
                    <>
                        <MaterialIcon name="progress_activity" className="animate-spin" />
                        {t('form.sending')}
                    </>
                ) : (
                    <>
                        <MaterialIcon name="send" />
                        {t('form.submit')}
                    </>
                )}
            </button>
        </form>
    )
}
